<template>
  <div>
    <!-- 제목 -->
    <v-sheet outlined class="d-flex align-center ma-2 px-4" height="60">
      <font class="font-weight-bold text-h6 grey--text text--darken-2">주인공 모집 목록</font>
    </v-sheet>

    <!-- 본문 -->
    <v-sheet outlined class="pa-2 ma-2">
      <!-- 검색 -->
      <v-sheet outlined class="pa-2 mb-2 d-flex">
        <v-sheet class="d-flex" width="500">
          <v-text-field placeholder="이름, 연락처, 특징으로 검색" outlined dense hide-details v-model="keyword" @keyup.enter="search()"></v-text-field>
          <v-btn height="40" class="px-6 ml-3" color="primary" dark depressed @click="search()">
            <v-icon small left>mdi-magnify</v-icon>
            검색하기
          </v-btn>
        </v-sheet>
        <v-spacer></v-spacer>
        <v-btn height="40" class="px-3" color="primary" dark depressed @click="refresh()">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-sheet>

      <!-- 목록 -->
      <v-data-table
        :headers="headers"
        :items="list"
        :server-items-length="total"
        :options.sync="options"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [10, 20, 50, -1],
          itemsPerPageText: '페이지당 항목 수',
        }"
        no-data-text="등록된 신청이 없습니다"
        @click:row="openDetail"
        class="row-pointer"
      >
        <template v-slot:item.role="{ item }">
          <v-chip small :color="item.role === 'mc' ? 'blue' : 'purple'" dark>
            {{ item.role === "mc" ? "MC" : "출연자" }}
          </v-chip>
        </template>
        <template v-slot:item.photo_1="{ item }">
          <v-img v-if="item.photo_1" :src="'/upload/website_youtube_link/' + item.photo_1" width="50" height="50" class="rounded my-1" cover></v-img>
          <span v-else class="grey--text text-caption">없음</span>
        </template>
        <template v-slot:item.created="{ item }">
          {{ formatDate(item.created) }}
        </template>
      </v-data-table>
    </v-sheet>

    <!-- 상세 보기 다이얼로그 -->
    <v-dialog v-model="dialog" max-width="900" scrollable>
      <v-card v-if="selected">
        <v-card-title class="d-flex justify-space-between">
          <span>신청 상세 보기</span>
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-simple-table>
            <tbody>
              <tr>
                <td class="font-weight-bold" width="120">등록일</td>
                <td>{{ formatDate(selected.created) }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">역할</td>
                <td>
                  <v-chip small :color="selected.role === 'mc' ? 'blue' : 'purple'" dark>
                    {{ selected.role === "mc" ? "MC" : "출연자" }}
                  </v-chip>
                </td>
              </tr>
              <tr>
                <td class="font-weight-bold">이름</td>
                <td>{{ selected.name }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">연락처</td>
                <td>{{ selected.contact }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">연령대</td>
                <td>{{ selected.age_range }}</td>
              </tr>
              <tr v-if="selected.guardian_contact">
                <td class="font-weight-bold">보호자 연락처</td>
                <td>{{ selected.guardian_contact }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">나만의 특징</td>
                <td style="white-space: pre-wrap">{{ selected.my_trait }}</td>
              </tr>
              <tr v-if="selected.video_url">
                <td class="font-weight-bold">영상 링크</td>
                <td>
                  <a :href="selected.video_url" target="_blank" class="text-decoration-none">
                    {{ selected.video_url }}
                    <v-icon x-small>mdi-open-in-new</v-icon>
                  </a>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <!-- 사진 -->
          <div class="mt-8 mb-12">
            <p class="font-weight-bold mb-2">첨부 사진</p>
            <div class="d-flex flex-wrap" style="gap: 12px">
              <v-card v-for="photo in selectedPhotos" :key="photo" flat outlined class="rounded-lg" style="overflow: hidden">
                <v-img :src="'/upload/website_youtube_link/' + photo" width="160" height="200" cover @click="previewPhoto = '/upload/website_youtube_link/' + photo" style="cursor: pointer"></v-img>
              </v-card>
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4 d-flex justify-end">
          <v-btn color="red" dark depressed @click="deleteItem(selected)">
            <v-icon small left>mdi-delete-outline</v-icon>
            삭제하기
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 사진 확대 보기 -->
    <v-dialog :value="!!previewPhoto" max-width="800" @input="previewPhoto = null">
      <v-img v-if="previewPhoto" :src="previewPhoto" contain max-height="80vh" @click="previewPhoto = null"></v-img>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data: () => ({
    keyword: "",
    list: [],
    total: 0,
    loading: false,
    options: {},

    headers: [
      { text: "등록일", value: "created", width: "140" },
      { text: "역할", value: "role", width: "100" },
      { text: "이름", value: "name", width: "120" },
      { text: "연락처", value: "contact", width: "180" },
      { text: "연령대", value: "age_range", width: "100" },
      { text: "사진", value: "photo_1", width: "70", sortable: false },
    ],

    dialog: false,
    selected: null,
    previewPhoto: null,
  }),

  computed: {
    selectedPhotos() {
      if (!this.selected) return []
      return [this.selected.photo_1, this.selected.photo_2, this.selected.photo_3].filter(Boolean)
    },
  },

  watch: {
    options: {
      handler() {
        this.load()
      },
      deep: true,
    },
  },

  methods: {
    load() {
      this.loading = true
      const { page, itemsPerPage } = this.options

      this.$http
        .post("/api/website_youtube_link/select", {
          params: {
            page: page || 1,
            itemsPerPage: itemsPerPage || 10,
            keyword: this.keyword || undefined,
          },
        })
        .then((res) => {
          this.list = res.data.list || []
          this.total = res.data.total || 0
        })
        .catch((err) => {
          console.error("Failed to load:", err)
        })
        .finally(() => {
          this.loading = false
        })
    },

    search() {
      this.options.page = 1
      this.load()
    },

    refresh() {
      this.keyword = ""
      this.options.page = 1
      this.load()
    },

    openDetail(item) {
      this.selected = item
      this.dialog = true
    },

    deleteItem(item) {
      if (!confirm("정말 해당 신청을 삭제하겠습니까?")) return
      this.$http
        .post("/api/website_youtube_link/delete", {
          params: { id: item.id },
        })
        .then((res) => {
          if (res.data.affectedRows) {
            this.dialog = false
            this.selected = null
            this.load()
          }
        })
        .catch((err) => {
          console.error("Failed to delete:", err)
        })
    },

    formatDate(dateStr) {
      if (!dateStr) return ""
      const d = new Date(dateStr)
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, "0")
      const day = String(d.getDate()).padStart(2, "0")
      const h = String(d.getHours()).padStart(2, "0")
      const min = String(d.getMinutes()).padStart(2, "0")
      return `${y}-${m}-${day} ${h}:${min}`
    },
  },
}
</script>

<style scoped>
.row-pointer >>> tbody tr {
  cursor: pointer;
}
.row-pointer >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}
</style>
